import React, { Component, Fragment } from 'react';
import { Button, Carousel, CarouselDots, CarouselArrow } from '@keethealth/keet-ui';
import FormQuestion from 'scenes/Questionnaire/components/FormQuestion';
import Panel from 'common/Panel';
import _ from 'lodash';
import questionnaire from 'test_constants/observation';
import Questions from 'scenes/Questionnaire/components/Questions';
import { optionToValue, getQuestionsObject } from 'utils/form';
import { checkHasStarted } from 'utils/utils';
import 'scenes/Questionnaire/Questionnaire.css';
import update from 'immutability-helper';
import PropTypes from 'prop-types';
import mutations from 'graph/mutations';
import { graphql, compose } from 'react-apollo';
import withModal from 'hocs/withModal';
import CompletedModal from 'common/CompletedModal';
import ObservationSummary from './ObservationSummary';
import QuestionHeight from './QuestionHeight';
import QuestionNumber from './QuestionNumber';
import './ObservationModal.css';

export class ObservationModal extends Component {
  static defaultProps = {
    isModalOpen: false,
  }

  static propTypes = {
    isModalOpen: PropTypes.bool,
    toggleModal: PropTypes.func.isRequired,
    carePlanId: PropTypes.string.isRequired,
    createObservation: PropTypes.func.isRequired,
  }

  static getDerivedStateFromProps(props, state) {
    const { questionsCompleted, questionsObject } = state;
    if ((questionsCompleted.length === 0 || Object.keys(questionsObject).length === 0)) {
      const newForm = questionnaire;
      return {
        ...state,
        questionsCompleted: Array(newForm.plannable.fhir.item.length).fill(false),
        questionsObject: getQuestionsObject(newForm.plannable.fhir.item),
      };
    }
    return {
      ...state,
    };
  }

  state = {
    questionIndexInt: 0,
    questionsCompleted: [],
    completed: false,
    questionsObject: {},
    isModalOpen: false,
    isCompletedModalOpen: false,
  }

  componentWillUnmount = () => {
    window.removeEventListener('beforeunload', this.handleAlertBeforeClose);
  };

  handleAlertBeforeClose = (ev) => {
    ev.preventDefault();
    return ev.returnValue = 'Changes you made may not be saved.';
  }

  handleMoveToQuestion = (targetIndex) => {
    this.setState({
      questionIndexInt: targetIndex,
    });
  }

  handleNext = () => {
    const { questionIndexInt } = this.state;
    const { plannable: { fhir: { item } } } = questionnaire;
    const lastIndex = item.length - 1;
    if (questionIndexInt < lastIndex) {
      this.handleMoveToQuestion(questionIndexInt + 1);
    }
  }

  handlePrev = () => {
    const { questionIndexInt } = this.state;
    if (questionIndexInt > 0) {
      this.handleMoveToQuestion(questionIndexInt - 1);
    }
  }

  handleSetQuestion = (questionIndex) => {
    const questionIndexInt = parseInt(questionIndex, 10);
    this.handleMoveToQuestion(questionIndexInt);
  }

  handleComplete = () => {
    const { questionIndexInt } = this.state;
    const { questionsCompleted } = this.state;
    this.setState({
      questionsCompleted: update(questionsCompleted, {
        [questionIndexInt]: { $set: true },
      }),
    });
  }

  handleIncomplete = () => {
    const { questionIndexInt } = this.state;
    const { questionsCompleted } = this.state;
    this.setState({
      questionsCompleted: update(questionsCompleted, {
        [questionIndexInt]: { $set: false },
      }),
    });
  }

  handleAnswer = (questionId, questionAnswer) => {
    const { questionsObject } = this.state;
    this.setState({
      questionsObject: update(questionsObject, {
        [questionId]: { answer: { $set: questionAnswer } },
      }),
    });
  }

  handleViewSummary = () => {
    this.handleMoveToQuestion('summary');
  }

  handleMarkAsComplete = () => {
    const { questionsObject } = this.state;
    const { carePlanId, createObservation } = this.props;
    _.map(questionsObject, (question) => {
      const { code, answer: { value, valueType } } = question;
      createObservation({ carePlanId, code, valueType, value });
    });
    this.setState({
      isCompletedModalOpen: true,
    });
  }

  renderQuestion = (question) => {
    const { questionsObject } = this.state;
    const answer = questionsObject[question.linkId].answer || undefined;

    const questionProps = {
      key: question.linkId,
      text: question.text,
      id: question.linkId,
      required: question.required,
      onComplete: this.handleComplete,
      onIncomplete: this.handleIncomplete,
      onAnswer: this.handleAnswer,
      onNext: this.handleNext,
      answer,
    };

    switch (question.type) {
      case 'integer':
        return (
          <Questions.Slider {...questionProps} />
        );
      case 'string':
        return (
          <Questions.String {...questionProps} />
        );
      case 'text':
        return (
          <Questions.Text {...questionProps} />
        );
      case 'choice':
        return question.repeats
          ? <Questions.MultiChoice {...questionProps} option={question.option.map(optionToValue)} />
          : <Questions.SingleChoice {...questionProps} option={question.option.map(optionToValue)} />;
      case 'display':
        return (
          <Questions.Display {...questionProps} />
        );
      case 'height':
        return (
          <QuestionHeight {...questionProps} />
        );
      case 'number':
        return (
          <QuestionNumber {...questionProps} />
        );
      case 'date':
        return (
          <Questions.Date {...questionProps} />
        );
      default:
        break;
    }
    return <noscript key="noscript" />;
  }

  renderQuestions = () => {
    const { plannable: { fhir: { item } } } = questionnaire;

    return item.map(question => this.renderQuestion(question));
  }

  renderButtons = () => {
    const { questionsCompleted, questionIndexInt } = this.state;
    const completed = questionsCompleted[questionIndexInt];
    return (
      <Fragment>
        <div className="w3 mh1">
          <CarouselArrow
            direction="left"
            hidden={questionIndexInt === 0}
            onClick={this.handlePrev}
          />
        </div>
        <div className="w3 mh1">
          {completed &&
            <CarouselArrow
              direction="right"
              hidden={questionIndexInt === questionsCompleted.length - 1 || questionsCompleted.length === 0}
              onClick={this.handleNext}
            />
          }
        </div>
      </Fragment>
    );
  }

  render() {
    const {
      questionIndexInt,
      questionsCompleted,
      questionsObject,
      isCompletedModalOpen,
    } = this.state;
    const { toggleModal } = this.props;

    const isFinished = questionsCompleted.length > 0 && !_.includes(questionsCompleted, false);
    const { plannable: { fhir: { item } } } = questionnaire;

    const hasStarted = checkHasStarted(questionIndexInt, questionsCompleted, questionsObject);

    if (hasStarted) {
      window.addEventListener('beforeunload', this.handleAlertBeforeClose);
    } else {
      window.removeEventListener('beforeunload', this.handleAlertBeforeClose);
    }

    return (
      <Fragment>
        <Panel id="scene-form" className="flex flex-column h-100 relative observationModal">
          {questionIndexInt === 'summary'
            ? (
              <FormQuestion title={'Observation'}>
                <ObservationSummary
                  questionnaireId={'0'}
                  onSetQuestion={this.handleSetQuestion}
                  questionsObject={questionsObject}
                  nextActivityId={''}
                  handleMarkAsComplete={this.handleMarkAsComplete}
                />
                <CompletedModal isOpen={isCompletedModalOpen} onClose={toggleModal} />
              </FormQuestion>
            )
            : (
              <FormQuestion title={'Observation'}>
                <CarouselDots
                  length={item.length}
                  completedSlides={questionsCompleted}
                  currentSlide={questionIndexInt}
                />
                <Carousel
                  className="w-100 flex-auto flex flex-column justify-center pb5"
                  controlled
                  currentIndex={questionIndexInt}
                >
                  {this.renderQuestions()}
                </Carousel>
                <div className="dn dn-m db-l w-100 absolute">{this.renderButtons()}</div>
                <div className="flex justify-center dn-l mb3">{this.renderButtons()}</div>
                {isFinished &&
                  <Button
                    className="center mb5 mt3"
                    onClick={this.handleViewSummary}
                  >
                    VIEW SUMMARY
                    <i className="fas fa-list-ol ml2" />
                  </Button>
                }
              </FormQuestion>
            )
          }
        </Panel>
      </Fragment>
    );
  }
}

export default compose(
  graphql(mutations.createObservation, {
    props: ({ mutate }) => ({
      createObservation: ({ carePlanId, code, valueType, value }) => mutate({
        variables: { input: { carePlanId, code, valueType, value, status: 'final' } },
      }),
    }),
  }),
  withModal,
)(ObservationModal);
